// SubSystemPicker — second step of the "create new asset from a no-
// match search" flow inside AddAssetDialog. The backend
// (`POST /capture/field-additions`) requires a parent sub-system
// because field staff may only add asset-type variants under an
// existing CCL sub-system (PRD §6.5, AGENTS.md §Field Additions). When
// the field-staff user types a query that returns zero matches we have
// no anchor sub-system to pre-select for them, so they pick one here
// before filling the rest of the variant form.
//
// Loads the full system → sub-system tree on mount. In production the
// loaders default to a build-aware router (default-classification-tree.ts):
// the local SQLite mirror on PowerSync/offline builds, the REST
// classification endpoints otherwise. Both loaders are also exposed as
// props so the picker is exercised in isolation without HTTP mocks.

import { useMemo, useState, type ReactElement } from 'react'

import { ApiError, isAbortError } from '../../hooks/api'
import { useAbortableEffect } from '../../hooks/use-abortable-effect'
import {
  type AssetSubSystemListRow,
  type AssetSystemListRow,
} from '../../hooks/use-classification'
import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { Spinner } from '../../ui/spinner'

import {
  defaultLoadSubSystems,
  defaultLoadSystems,
} from './default-classification-tree'

export interface SubSystemPick {
  subSystemId: string
  subSystemName: string
  // Parent system name — surfaced so the variant form's
  // breadcrumb pill ("Saving under <System> > <Sub-system>") can
  // show the full taxonomy slot the user just chose.
  systemName: string
}

export interface SubSystemPickerProps {
  // Pre-fill the heading so the user remembers which name they're
  // about to file.
  assetName: string
  onPick: (pick: SubSystemPick) => void
  onCancel: () => void
  // Test seams — production defaults to the build-aware classification
  // loaders (local SQLite offline, REST online).
  loadSystems?: (init: { signal: AbortSignal }) => Promise<AssetSystemListRow[]>
  loadSubSystems?: (
    systemId: string,
    init: { signal: AbortSignal },
  ) => Promise<AssetSubSystemListRow[]>
}

interface SystemWithSubs {
  systemId: string
  systemName: string
  subSystems: AssetSubSystemListRow[]
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; tree: SystemWithSubs[] }
  | { status: 'error'; message: string }

const LOADING_STATE: LoadState = { status: 'loading' }

function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unexpected error'
}

export function SubSystemPicker({
  assetName,
  onPick,
  onCancel,
  loadSystems = defaultLoadSystems,
  loadSubSystems = defaultLoadSubSystems,
}: SubSystemPickerProps): ReactElement {
  const [state, setState] = useState<LoadState>(LOADING_STATE)

  useAbortableEffect(
    (signal) => {
      setState(LOADING_STATE)
      loadSystems({ signal })
        .then(async (systems) => {
          const subLists = await Promise.all(
            systems.map((s) =>
              loadSubSystems(s.id, { signal }).then((subs) => ({
                systemId: s.id,
                systemName: s.name,
                subSystems: subs,
              })),
            ),
          )
          if (signal.aborted) return
          const populated = subLists.filter((entry) => entry.subSystems.length > 0)
          setState({ status: 'ready', tree: populated })
        })
        .catch((err: unknown) => {
          if (isAbortError(err)) return
          setState({ status: 'error', message: describeError(err) })
        })
      return undefined
    },
    [loadSystems, loadSubSystems],
  )

  const totalSubSystems = useMemo(() => {
    if (state.status !== 'ready') return 0
    return state.tree.reduce((acc, sys) => acc + sys.subSystems.length, 0)
  }, [state])

  return (
    <section
      aria-labelledby="sub-system-picker-heading"
      className="space-y-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div>
        <h3
          id="sub-system-picker-heading"
          className="text-base font-semibold text-gray-900"
        >
          Pick a category for “{assetName}”
        </h3>
        <p className="mt-1 text-xs text-gray-600">
          Field-added assets must sit under an existing category so admin
          review can place them correctly.
        </p>
      </div>

      {state.status === 'loading' ? (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Spinner label="Loading categories" size="sm" /> Loading categories…
        </div>
      ) : null}

      {state.status === 'error' ? (
        <Alert variant="error" title="Could not load categories">
          {state.message}
        </Alert>
      ) : null}

      {state.status === 'ready' && totalSubSystems === 0 ? (
        <Alert variant="info" title="No categories available">
          No asset categories are configured for this contract. Ask an admin
          to set up the asset taxonomy before adding new assets.
        </Alert>
      ) : null}

      {state.status === 'ready' && totalSubSystems > 0 ? (
        <ul className="flex max-h-[50vh] flex-col gap-4 overflow-y-auto">
          {state.tree.map((system) => (
            <li key={system.systemId}>
              <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {system.systemName}
              </h4>
              <ul className="mt-1 flex flex-col gap-1">
                {system.subSystems.map((sub) => (
                  <li key={sub.id}>
                    <button
                      type="button"
                      onClick={() =>
                        onPick({
                          subSystemId: sub.id,
                          subSystemName: sub.name,
                          systemName: system.systemName,
                        })
                      }
                      className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand)]"
                    >
                      <span className="font-medium">{sub.name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  )
}
